(() => {
  if (window.NubsSessionState) return;

  const CACHE_VERSION = 2;
  const PROFILE_KEY = 'nubs-media-session-profile-v1';
  const COLLECTION_KEY = 'nubs-media-session-collection-v1';
  const VISIBILITY_CACHE_KEY = 'nubs:catalog-hidden-release-ids:v1';
  const VISIBILITY_TOKEN_KEY = 'nubs:catalog-visibility-token:v1';
  const VISIBILITY_MAX_AGE_MS = 30 * 60 * 1000;
  const DISPLAY_MAX_AGE_MS = 2 * 60 * 1000;
  const REFRESH_AFTER_MS = 30 * 1000;
  const CLEAR_SIGNAL_KEY = 'nubs-media-session-clear-v1';
  const CHANNEL_NAME = 'nubs-media-session-state-v1';
  const AUTH_PRESENTATION_KEY = 'nubs-media-auth-presentation-v1';

  const script = document.currentScript || document.querySelector('[data-nubs-session-state]');
  const configuredBridge = String(script?.dataset?.bridgeUrl || '').trim().replace(/\/+$/, '');
  const inFlight = {profile: null, collection: null, visibility: null};
  let channel = null;
  let sessionGeneration = 0;
  let fullProfile = null;
  let profileRequestFull = false;
  let collectionRevision = 0;
  // No private cache is trusted until this document has verified the session.
  let verifiedOwner = null;
  let lastProfileVerification = 0;
  let identityCheck = null;
  // A tab restored from the back/forward cache might have missed cross-tab
  // messages while frozen. Retain the latest clear signal for comparison.
  let lastClearSignal = null;
  try { lastClearSignal = localStorage.getItem(CLEAR_SIGNAL_KEY); } catch (_) {}

  function checkRemoteClearSignal() {
    let signal = null;
    try { signal = localStorage.getItem(CLEAR_SIGNAL_KEY); } catch (_) { return false; }
    if (!signal || signal === lastClearSignal) return false;
    lastClearSignal = signal;
    clearLocal();
    return true;
  }

  try {
    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.addEventListener('message', (event) => {
        if (event?.data?.type === 'clear') {
          const signal = String(event.data.signal || '');
          if (signal && signal === lastClearSignal) return;
          lastClearSignal = signal || lastClearSignal;
          clearLocal();
        }
      });
    }
  } catch (_) {}

  function now() {
    return Date.now();
  }

  function bridgeBase() {
    if (configuredBridge) return configuredBridge;
    const root = document.querySelector('[data-bridge-url]');
    return String(root?.dataset?.bridgeUrl || '').trim().replace(/\/+$/, '');
  }

  function readRaw(key) {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!parsed || parsed.version !== CACHE_VERSION || !Number.isFinite(Number(parsed.savedAt))) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function writeRaw(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify({
        version: CACHE_VERSION,
        savedAt: now(),
        value,
      }));
    } catch (_) {}
  }

  function readFresh(key, maxAge = DISPLAY_MAX_AGE_MS) {
    const item = readRaw(key);
    if (!item) return null;
    if (now() - Number(item.savedAt) > maxAge) return null;
    return item;
  }

  function staleEnoughToRefresh(key) {
    const item = readRaw(key);
    return !item || now() - Number(item.savedAt) > REFRESH_AFTER_MS;
  }

  // Presentation-only memory for auth-dependent chrome. This never grants access
  // and never replaces a bridge check; it only lets pages paint a previously
  // confirmed state without visually relearning it on every navigation.
  function readAuthPresentation() {
    try {
      const parsed = JSON.parse(localStorage.getItem(AUTH_PRESENTATION_KEY) || 'null');
      if (!parsed || parsed.version !== 1) return null;
      if (parsed.state !== 'signed-in' && parsed.state !== 'signed-out') return null;
      return {state: parsed.state, confirmedAt: Number(parsed.confirmedAt) || 0};
    } catch (_) {
      return null;
    }
  }

  function writeAuthPresentation(state) {
    try {
      if (state !== 'signed-in' && state !== 'signed-out') {
        localStorage.removeItem(AUTH_PRESENTATION_KEY);
        return null;
      }
      const value = {version: 1, state, confirmedAt: now()};
      localStorage.setItem(AUTH_PRESENTATION_KEY, JSON.stringify(value));
      return value;
    } catch (_) {
      return null;
    }
  }

  function clearAuthPresentation() {
    try { localStorage.removeItem(AUTH_PRESENTATION_KEY); } catch (_) {}
  }

  function sanitizeProfile(payload) {
    const profile = payload?.profile && typeof payload.profile === 'object'
      ? payload.profile
      : null;

    return {
      authenticated: true,
      profileFound: Boolean(payload?.profileFound && profile),
      profile: profile ? {
        id: String(profile.id || ''),
        username: String(profile.username || ''),
        usernameType: String(profile.usernameType || ''),
        createdAt: profile.createdAt || null,
        usernameChange: profile.usernameChange && typeof profile.usernameChange === 'object'
          ? {
              limit: profile.usernameChange.limit,
              remaining: profile.usernameChange.remaining,
              windowHours: profile.usernameChange.windowHours,
              resetsAt: profile.usernameChange.resetsAt || null,
            }
          : null,
        // Previous usernames are intentionally not persisted in browser state.
        previousUsernames: [],
      } : null,
    };
  }

  function sanitizeCollection(payload) {
    // /bridge/state historically did not always include an explicit `authenticated`
    // boolean on successful authenticated responses. A successful state payload with
    // collection arrays is authenticated; 401 paths are written explicitly as false.
    const authenticated = typeof payload?.authenticated === 'boolean'
      ? payload.authenticated
      : Boolean(payload?.ok || Array.isArray(payload?.ownedIds) || Array.isArray(payload?.wantedIds));

    return {
      authenticated,
      ownedIds: authenticated && Array.isArray(payload?.ownedIds) ? payload.ownedIds.filter((id) => typeof id === 'string') : [],
      wantedIds: authenticated && Array.isArray(payload?.wantedIds) ? payload.wantedIds.filter((id) => typeof id === 'string') : [],
      coveredIds: authenticated && Array.isArray(payload?.coveredIds) ? payload.coveredIds.filter((id) => typeof id === 'string') : [],
      showCoveredByCollection: authenticated && payload?.showCoveredByCollection === true,
    };
  }

  function readProfile() {
    return verifiedOwner === null ? null : readFresh(PROFILE_KEY)?.value || null;
  }

  function readCollection() {
    const value = readFresh(COLLECTION_KEY)?.value;
    return verifiedOwner && value?.ownerId === verifiedOwner ? value : null;
  }

  function writeProfile(payload) {
    fullProfile = null;
    const value = payload?.authenticated === false
      ? {authenticated: false, profileFound: false, profile: null}
      : sanitizeProfile(payload);
    const previous = readRaw(PROFILE_KEY)?.value;
    const previousOwner = previous?.profile?.id || verifiedOwner;
    const nextOwner = value.authenticated ? String(value.profile?.id || '') : '';
    if ((previous?.authenticated && !value.authenticated)
      || (previousOwner && previousOwner !== nextOwner)) {
      clearAll();
    }
    verifiedOwner = nextOwner;
    lastProfileVerification = now();
    writeRaw(PROFILE_KEY, value);
    try { document.dispatchEvent(new CustomEvent('nubs:profile-state', {detail: value})); } catch (_) {}
    return value;
  }

  function writeCollection(payload) {
    collectionRevision++;
    const value = sanitizeCollection(payload);
    if (!value.authenticated) {
      writeProfile({authenticated:false});
      try { sessionStorage.removeItem(COLLECTION_KEY); } catch (_) {}
      return value;
    }
    if (verifiedOwner) writeRaw(COLLECTION_KEY, {...value, ownerId:verifiedOwner});
    return value;
  }

  function clearLocal() {
    sessionGeneration++;
    collectionRevision++;
    verifiedOwner = null;
    lastProfileVerification = 0;
    clearAuthPresentation();
    fullProfile = null;
    inFlight.profile = null; inFlight.collection = null; inFlight.visibility = null;
    try {
      sessionStorage.removeItem(PROFILE_KEY);
      sessionStorage.removeItem(COLLECTION_KEY);
      sessionStorage.removeItem(VISIBILITY_CACHE_KEY);
      sessionStorage.removeItem(VISIBILITY_TOKEN_KEY);
      sessionStorage.removeItem('nubs-header-account-badges-v1');
      for (let i=sessionStorage.length-1; i>=0; i--) {
        const key=sessionStorage.key(i);
        if (key && (key.startsWith('nubs-header-') || key.startsWith('nubs-account-') || key.startsWith('nubs-public-profile-'))) sessionStorage.removeItem(key);
      }
    } catch (_) {}
    try { document.dispatchEvent(new CustomEvent('nubs:session-cleared')); } catch (_) {}
    try { document.dispatchEvent(new CustomEvent('nubs:profile-state', {detail: {authenticated:false, profileFound:false, profile:null}})); } catch (_) {}
  }

  function clearAll({broadcast = true} = {}) {
    clearLocal();
    if (broadcast) {
      // Write a durable signal before notifying other tabs; it also catches
      // signouts that happened while another tab was frozen in bfcache.
      const signal = `${now()}:${Math.random()}`;
      lastClearSignal = signal;
      try { localStorage.setItem(CLEAR_SIGNAL_KEY, signal); } catch (_) {}
      try { channel?.postMessage({type: 'clear', signal}); } catch (_) {}
    }
  }

  async function readJson(response) {
    return response.json().catch(() => null);
  }

  async function timedFetch(url, options = {}, timeoutMs = 8000) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response=await fetch(url, {...options, signal:controller.signal});
      const bytes=await response.arrayBuffer();
      return new Response(bytes,{status:response.status,statusText:response.statusText,headers:response.headers});
    } finally {
      window.clearTimeout(timer);
    }
  }

  async function fetchProfile({force = false, full = Boolean(document.querySelector('[data-nubs-account-page]'))} = {}) {
    if (full && !force && fullProfile && now()-fullProfile.time < REFRESH_AFTER_MS) return fullProfile.value;
    if (!force && !full && verifiedOwner !== null) {
      const cached = readFresh(PROFILE_KEY, REFRESH_AFTER_MS);
      if (cached) return cached.value;
    }
    if (inFlight.profile) {
      if (full && !profileRequestFull) { await inFlight.profile.catch(()=>null); return fetchProfile({full:true,force}); }
      return inFlight.profile;
    }
    profileRequestFull = full;
    const bridge = bridgeBase();
    if (!bridge) return null;

    const epoch = sessionGeneration;
    inFlight.profile = (async () => {
      const response = await timedFetch(`${bridge}/bridge/profile-state${full ? '' : '?summary=1'}`, {
        method: 'GET',
        credentials: 'include',
        headers: {'Accept': 'application/json'},
        cache: 'no-store',
      });

      if (epoch !== sessionGeneration) return null;
      if (response.status === 401) {
        return writeProfile({authenticated: false});
      }

      const payload = await readJson(response);
      if (epoch !== sessionGeneration) return null;
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || `Account service returned HTTP ${response.status}.`);
      }

      writeProfile(payload);
      if (full) fullProfile={time:now(),value:payload};
      return payload;
    })().finally(() => {
      if (epoch === sessionGeneration) inFlight.profile = null;
    });

    return inFlight.profile;
  }

  async function fetchCollection({force = false} = {}) {
    if (verifiedOwner === null) await fetchProfile({force:true,full:false});
    if (verifiedOwner === null) return null;
    if (readProfile()?.authenticated === false) return sanitizeCollection({authenticated:false});
    if (!force) {
      const cached = readFresh(COLLECTION_KEY, REFRESH_AFTER_MS);
      if (verifiedOwner && cached?.value?.ownerId === verifiedOwner) return cached.value;
    }
    if (inFlight.collection) return inFlight.collection;
    const bridge = bridgeBase();
    if (!bridge) return null;

    const epoch = sessionGeneration;
    const revision = collectionRevision;
    inFlight.collection = (async () => {
      const response = await timedFetch(`${bridge}/bridge/state`, {
        method: 'GET',
        credentials: 'include',
        headers: {'Accept': 'application/json'},
        cache: 'no-store',
      });

      if (epoch !== sessionGeneration) return null;
      if (response.status === 401) {
        return writeCollection({authenticated: false, ownedIds: [], wantedIds: [], coveredIds: [], showCoveredByCollection: false});
      }

      const payload = await readJson(response);
      if (epoch !== sessionGeneration) return null;
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || `Collection service returned HTTP ${response.status}.`);
      }

      if (revision !== collectionRevision) return readCollection();
      writeCollection(payload);
      return payload;
    })().finally(() => {
      if (epoch === sessionGeneration) inFlight.collection = null;
    });

    return inFlight.collection;
  }


  function hasFreshVisibilityCache() {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(VISIBILITY_CACHE_KEY) || 'null');
      return Boolean(parsed && Array.isArray(parsed.hiddenCatalogIds) && Number(parsed.expiresAt) > now());
    } catch (_) {
      return false;
    }
  }

  async function fetchVisibility({force = false} = {}) {
    if (hasFreshVisibilityCache() && !force) return null;
    if (inFlight.visibility) return inFlight.visibility;
    const bridge = bridgeBase();
    if (!bridge) return null;
    const epoch = sessionGeneration;
    inFlight.visibility = (async () => {
      const response = await timedFetch(`${bridge}/catalog/releases?mode=visibility-list`, {
        method: 'GET',
        credentials: 'include',
        headers: {'Accept': 'application/json'},
        cache: 'no-store',
      });
      const payload = await readJson(response);
      if (epoch !== sessionGeneration) return null;
      if (!response.ok || !payload?.ok || !Array.isArray(payload.hiddenCatalogIds)) return null;
      const expiresAt = now() + VISIBILITY_MAX_AGE_MS;
      try {
        sessionStorage.setItem(VISIBILITY_CACHE_KEY, JSON.stringify({
          hiddenCatalogIds: payload.hiddenCatalogIds,
          visibilityToken: String(payload.visibilityToken || ''),
          personalized: payload.personalized === true,
          catalogVersion: String(payload.catalogVersion || ''),
          expiresAt,
        }));
        sessionStorage.setItem(VISIBILITY_TOKEN_KEY, JSON.stringify({
          visibilityToken: String(payload.visibilityToken || ''),
          expiresAt,
        }));
      } catch (_) {}
      return payload;
    })().finally(() => { if (epoch === sessionGeneration) inFlight.visibility = null; });
    return inFlight.visibility;
  }

  let csrfPromise = null;
  let csrfValue = '';
  document.addEventListener('nubs:session-cleared', () => { csrfValue = ''; csrfPromise = null; });
  async function getCsrfToken({force = false} = {}) {
    if (csrfValue && !force) return csrfValue;
    if (csrfPromise) return csrfPromise;
    const epoch = sessionGeneration;
    const job = timedFetch(`${bridgeBase()}/bridge/csrf`, {credentials:'include', cache:'no-store', headers:{Accept:'application/json'}})
      .then(async response => {
        const body = await readJson(response);
        if (epoch !== sessionGeneration) throw new Error('Account session changed. Please try again.');
        if (!response.ok || !body?.csrfToken) { const error = new Error(body?.error || 'Sign in and try again.'); error.code = response.status === 401 ? 'AUTH_REQUIRED' : 'CSRF_FAILED'; throw error; }
        csrfValue = String(body.csrfToken); return csrfValue;
      }).finally(() => { if (csrfPromise === job) csrfPromise = null; });
    csrfPromise = job; return job;
  }


  function privateScope(root) {
    const epoch = sessionGeneration;
    const controller = new AbortController();
    let invalidated = false;
    let shielded = false;
    let previousVisibility = '';
    let previousBusy = null;
    let restoreNotice = null;
    const active = () => !invalidated && epoch === sessionGeneration && root.isConnected !== false;
    const assertCurrent = () => {
      if (!active()) {
        const error = new Error('Your session changed. Refresh this page to continue.');
        error.code = 'SESSION_CHANGED';
        throw error;
      }
    };
    const showRestoreNotice = (retry = false) => {
      if (!restoreNotice) {
        restoreNotice = document.createElement('div');
        restoreNotice.setAttribute('role','status');
        restoreNotice.style.cssText='position:fixed;bottom:18px;left:50%;transform:translateX(-50%);z-index:2147483000;max-width:calc(100vw - 32px);padding:12px 18px;border:1px solid #cddde9;border-radius:9px;background:#fff;color:#18384d;box-shadow:0 4px 20px #0002;text-align:center;font:600 14px/1.5 system-ui,sans-serif';
        document.body.appendChild(restoreNotice);
      }
      restoreNotice.replaceChildren();
      restoreNotice.append(document.createTextNode(retry?'Could not verify your session. ':'Checking your account session…'));
      if (retry) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = 'Retry';
        button.style.cssText='margin-left:10px;padding:5px 12px;border-radius:5px;border:1px solid #9cbed3;background:#f0f8fd;color:#164467;cursor:pointer';
        button.addEventListener('click', () => { if (active()) revalidateSession(); });
        restoreNotice.append(button);
      }
    };
    const unshield = () => {
      if (!shielded) return;
      root.style.visibility = previousVisibility;
      if (previousBusy === null) root.removeAttribute('aria-busy');
      else root.setAttribute('aria-busy',previousBusy);
      restoreNotice?.remove();
      restoreNotice=null;
      shielded=false;
    };
    // Only a bfcache-restored document needs a privacy shield while its old
    // session is validated. `visibility:hidden` retains layout and scroll,
    // unlike root.hidden=true (which caused the account jump-to-top).
    const shieldRestoredPage = () => {
      if (!active() || shielded) return;
      previousVisibility=root.style.visibility;
      previousBusy=root.getAttribute('aria-busy');
      shielded=true;
      root.style.visibility='hidden';
      root.setAttribute('aria-busy','true');
      showRestoreNotice();
    };
    const verified = () => { if (active()) unshield(); };
    const verificationFailed = () => { if (active() && shielded) showRestoreNotice(true); };
    const dispose = () => {
      document.removeEventListener('nubs:session-cleared',clear);
      document.removeEventListener('nubs:beforepagechange',leave);
      document.removeEventListener('nubs:session-restoring',shieldRestoredPage);
      document.removeEventListener('nubs:session-verified',verified);
      document.removeEventListener('nubs:session-verification-error',verificationFailed);
      unshield();
      controller.abort();
    };
    const clear = () => {
      if (invalidated) return;
      invalidated = true;
      root.querySelectorAll('dialog').forEach(dialog => {try {dialog.close();} catch (_) {} dialog.removeAttribute('open');});
      root.querySelectorAll('input, textarea, select').forEach(input => {input.value='';input.checked=false;});
      const notice = document.createElement('p');
      notice.setAttribute('role','status');
      notice.textContent = 'Your session changed. Refresh this page to continue.';
      const refresh = document.createElement('a');
      refresh.href = location.href;
      refresh.textContent = 'Refresh page';
      refresh.setAttribute('data-nubs-hard-navigation','');
      unshield();
      root.hidden = false;
      root.replaceChildren(notice,refresh);
      dispose();
    };
    const leave = () => {invalidated=true;dispose();};
    document.addEventListener('nubs:session-cleared',clear);
    document.addEventListener('nubs:beforepagechange',leave);
    document.addEventListener('nubs:session-restoring',shieldRestoredPage);
    document.addEventListener('nubs:session-verified',verified);
    document.addEventListener('nubs:session-verification-error',verificationFailed);
    return {
      get active() { return active(); },
      assertCurrent,
      async fetch(url,options={}) {
        if (identityCheck) await identityCheck;
        assertCurrent();
        // Preserve a caller's deadline as well as the private-scope abort. The
        // caller's signal must cover streaming response bodies, not just headers.
        let signal=controller.signal;
        let detach=()=>{};
        if(options.signal && options.signal!==controller.signal){
          const combined=new AbortController();
          const signals=[controller.signal,options.signal];
          const abort=()=>combined.abort();
          for(const source of signals){
            if(source.aborted)abort();
            else source.addEventListener('abort',abort,{once:true});
          }
          detach=()=>signals.forEach(source=>source.removeEventListener('abort',abort));
          signal=combined.signal;
        }
        let response;
        try{
          response=await window.fetch(url,{...options,cache:'no-store',signal});
          if (identityCheck) await identityCheck;
          assertCurrent();
        }catch(error){detach();throw error;}
        // Preserve native Response methods and guard each body read as well as
        // the headers. Session changes while JSON/CSV is downloading are caught.
        return new Proxy(response, {get(target,key) {
          const value = Reflect.get(target,key,target);
          if (['json','text','blob','arrayBuffer','formData'].includes(key)) return async (...args) => {
            try{
              assertCurrent();
              const body = await value.apply(target,args);
              if (identityCheck) await identityCheck;
              assertCurrent();
              return body;
            }finally{detach();}
          };
          return typeof value === 'function' ? value.bind(target) : value;
        }});
      },
    };
  }

  // Focus and visibility checks are deliberately non-destructive: they must
  // never hide/remount an active page or treat a network timeout as signout.
  // Explicit signout and a confirmed 401/unauthenticated profile still clear
  // the current tab and broadcast to other tabs as before.
  async function revalidateSession() {
    if (document.visibilityState === 'hidden') return;
    if (identityCheck) return identityCheck;
    document.dispatchEvent(new CustomEvent('nubs:session-verifying'));
    identityCheck = fetchProfile({force:true,full:false}).then(result => {
      if (!result || typeof result.authenticated !== 'boolean') {
        throw new Error('Session verification was inconclusive.');
      }
      document.dispatchEvent(new CustomEvent('nubs:session-verified'));
      return result;
    }).catch(() => {
      // Network errors, timeouts, invalid JSON and 5xx do NOT prove signout.
      // The bridge independently authorizes every private request.
      document.dispatchEvent(new CustomEvent('nubs:session-verification-error'));
      return null;
    }).finally(() => { identityCheck = null; });
    return identityCheck;
  }
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    if (checkRemoteClearSignal()) return;
    // A bfcache document can predate a real logout, so shield private data
    // WITHOUT collapsing its layout while checking the current identity.
    document.dispatchEvent(new CustomEvent('nubs:session-restoring'));
    revalidateSession();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (!checkRemoteClearSignal()) revalidateSession();
    }
  });
  window.addEventListener('storage', event => {
    if (event.key === CLEAR_SIGNAL_KEY && event.newValue) checkRemoteClearSignal();
  });
  window.addEventListener('focus', () => {
    if (checkRemoteClearSignal()) return;
    if (now()-lastProfileVerification > 5000) revalidateSession();
  });

  function prewarm() {
    const jobs = [];
    const needsCollection = Boolean(document.querySelector(
      '[data-nubs-collection-actions], [data-nubs-collection-page], [data-nubs-account-page]',
    ));
    const needsVisibility = Boolean(document.querySelector(
      '[data-media-visibility-root], [data-nubs-collection-page], [data-nubs-directory]',
    ));

    // The header needs profile state site-wide, but collection and visibility
    // data are only useful on pages that actually consume them. Avoiding those
    // two cross-origin bridge calls on ordinary content/store/forum pages keeps
    // warm navigation lighter without changing any authorization decisions.
    if (verifiedOwner === null || staleEnoughToRefresh(PROFILE_KEY)) jobs.push(fetchProfile().catch(() => null));
    if (needsCollection && staleEnoughToRefresh(COLLECTION_KEY)) jobs.push(fetchCollection().catch(() => null));
    if (needsVisibility && !hasFreshVisibilityCache()) jobs.push(fetchVisibility().catch(() => null));
    return Promise.all(jobs);
  }

  // Upgrade old, unbound caches before any page can consume them. Old public
  // profile cache entries also included the viewer's private block relationship.
  try {
    const previous = JSON.parse(sessionStorage.getItem(PROFILE_KEY) || 'null');
    const collection = JSON.parse(sessionStorage.getItem(COLLECTION_KEY) || 'null');
    if ((previous && previous.version !== CACHE_VERSION)
      || (collection && (collection.version !== CACHE_VERSION || !collection.value?.ownerId))) clearLocal();
    for (let i=sessionStorage.length-1; i>=0; i--) {
      const key=sessionStorage.key(i);
      if (key?.startsWith('nubs-public-profile-v2:')) sessionStorage.removeItem(key);
    }
  } catch (_) { clearLocal(); }

  window.NubsSessionState = {
    getCsrfToken,
    get generation() { return sessionGeneration; },
    privateScope,
    readProfile,
    readCollection,
    writeProfile,
    writeCollection,
    fetchProfile,
    fetchCollection,
    fetchVisibility,
    clearAll,
    prewarm,
    readAuthPresentation,
    writeAuthPresentation,
    clearAuthPresentation,
  };

  document.addEventListener('pointerdown', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    try {
      const url = new URL(link.href, location.href);
      if (/\/account\/login(?:$|[/?#])/i.test(url.pathname + url.search)) clearAuthPresentation();
    } catch (_) {}
  }, {capture: true, passive: true});

  const startPrewarm = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => prewarm(), {timeout: 1200});
    } else {
      window.setTimeout(() => prewarm(), 150);
    }
  };

  if (document.readyState === 'complete') startPrewarm();
  else window.addEventListener('load', startPrewarm, {once: true});
})();
